import {AssignedToType} from '@/entities/task';
import type Task from '@/entities/task';

export const TASK_ASSIGNEE_LOCKED_MESSAGE = 'The assignee cannot be changed after the timer has started.';

type TimerStartedTask = Pick<Task, 'timerStarted' | 'running' | 'lastStart'>;
type TimerAssignableTask = Pick<Task, 'assignedTo' | 'assignedToType'>;
export const TASK_ASSIGN_BEFORE_START = 'Assign this task before starting it.';
export const canStartTask = (task: TimerAssignableTask & Partial<TimerStartedTask>, userId?: string): boolean =>
    !!userId && userId !== '00000000-0000-0000-0000-000000000000'
    && (task.assignedToType == null || task.assignedToType === 'U' || task.assignedToType === 'G')
    && ((!!task.assignedTo && task.assignedTo !== '00000000-0000-0000-0000-000000000000')
        || !hasTaskTimerStarted(task));

/**
 * A task's first timer start permanently locks its assignee. timerStarted is
 * the durable marker; running and lastStart also cover an in-flight legacy
 * timer whose permanent marker has not reached this client yet.
 */
export const hasTaskTimerStarted = (task: Partial<TimerStartedTask>): boolean =>
    !!task.timerStarted || !!task.running || (task.lastStart ?? 0) > 0;

export const canReassignTasks = (taskOrTasks: TimerStartedTask | TimerStartedTask[]): boolean => {
    const tasks = Array.isArray(taskOrTasks) ? taskOrTasks : [taskOrTasks];
    return !tasks.some(hasTaskTimerStarted);
};

export const guardTaskReassignment = (
    taskOrTasks: TimerStartedTask | TimerStartedTask[],
    onLocked: (message: string) => void,
): boolean => {
    if (canReassignTasks(taskOrTasks)) return true;

    onLocked(TASK_ASSIGNEE_LOCKED_MESSAGE);
    return false;
};

/**
 * Starting or manually recording timer work claims a group-assigned or
 * unassigned task for the current user. Keep this predicate shared so the two
 * timer entry paths cannot diverge.
 */
export const canClaimTaskForTimerWork = (task: TimerAssignableTask): boolean =>
    task.assignedToType === AssignedToType.Group || !task.assignedTo
    || task.assignedTo === '00000000-0000-0000-0000-000000000000';
